import { ApplicationConfig } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Validators } from '@angular/forms';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { RECAPTCHA_V3_SITE_KEY, ReCaptchaV3Service, RecaptchaLoaderService } from 'ng-recaptcha';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideFormlyCore, FormlyFieldConfig } from '@ngx-formly/core';
import { withFormlyMaterial } from '@ngx-formly/material';
import { get } from 'lodash';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { FormlyFieldTabs } from './form/controls/tabs-type/tabs-type.component';
import { FormlyFieldFileComponent } from './form/controls/file-upload/file-upload.component';
import { FormlyFieldRichTextComponent } from './form/controls/rich-text-type/rich-text-type.component';
import { FormlyFieldDateTimeComponent } from './form/controls/date-time-type/date-time-type.component';
import { FormlyFieldTextContentComponent } from './form/controls/text-content/text-content.component';
import { FormlyFieldMultiSelectListComponent } from './form/controls/multi-select-list/multi-select-list.component';
import { FormlyFieldObjectChooserFieldComponent } from './form/controls/object-chooser-field/object-chooser-field.component';
import { FormlyFieldSectionHeaderComponent } from './form/controls/section-header/section-header.component';
import { MultiLineTextComponent } from './form/controls/multi-line-text/multi-line-text.component';
import { FormlyFieldSectionWrapperComponent } from './form/controls/section-wrapper/section-wrapper.component';
import { PhoneValidator, WebAddressValidator, maxValueError, minValueError, maxLengthError, minLengthError } from './form/validators';
import { CONTACT_SELF_SERVICE_PROVIDERS } from './form/contact-self-service-forms/contact-self-service-forms.providers';
import { ContactSelfServiceFormsService } from './form/contact-self-service-forms/contact-self-service-forms.service';

const preventEnterSubmit = {
  keydown: (_field: FormlyFieldConfig, event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.stopPropagation();
      event.preventDefault();
    }
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideNativeDateAdapter(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: `${environment.assetPathWithoutOrigin}i18n/`,
        suffix: '.json',
      }),
    }),
    provideFormlyCore([
      ...withFormlyMaterial(),
      {
        types: [
          {
            name: 'tabs',
            component: FormlyFieldTabs
          },
          {
            name: 'file-upload',
            component: FormlyFieldFileComponent,
            wrappers: ['form-field'],
          },
          {
            name: 'rich-text',
            component: FormlyFieldRichTextComponent,
            wrappers: ['form-field'],
          },
          {
            name: 'date-time',
            component: FormlyFieldDateTimeComponent,
            wrappers: ['form-field'],
            defaultOptions: {
              props: preventEnterSubmit,
            },
          },
          {
            name: 'text-content',
            component: FormlyFieldTextContentComponent,
          },
          {
            name: 'list-items-select',
            component: FormlyFieldMultiSelectListComponent,
            wrappers: ['form-field'],
          },
          {
            name: 'object-chooser',
            component: FormlyFieldObjectChooserFieldComponent,
            wrappers: ['form-field'],
          },
          {
            name: 'section-header',
            component: FormlyFieldSectionHeaderComponent,
          },
          {
            name: 'multi-line-text',
            component: MultiLineTextComponent,
            wrappers: ['form-field'],
          },
          {
            name: 'checkbox',
            extends: 'input',
            defaultOptions: {
              props: {
                type: 'checkbox',
              },
              expressions: {
                'props.disabled': (field: FormlyFieldConfig) => {
                  return field.props?.readonly;
                },
              },
            },
          },
          {
            name: 'number',
            extends: 'input',
            defaultOptions: {
              props: {
                type: 'number',
              },
              expressions: {
                'props.min': (field: FormlyFieldConfig) => {
                  return get(field, 'objectSchemaProperties.minimum', null);
                },
                'props.max': (field: FormlyFieldConfig) => {
                  return get(field, 'objectSchemaProperties.maximum', null);
                },
              },
            },
          },
          {
            name: 'phone-number',
            extends: 'input',
            defaultOptions: {
              validators: {
                validation: ['phone-number'],
              },
              props: preventEnterSubmit,
            },
          },
          {
            name: 'email-address',
            extends: 'input',
            defaultOptions: {
              validators: {
                validation: ['email'],
              },
              props: preventEnterSubmit,
            },
          },
          {
            name: 'web-address',
            extends: 'input',
            defaultOptions: {
              validators: {
                validation: ['webAddress'],
              },
              props: preventEnterSubmit,
            },
          },
        ],
        wrappers: [
          {
            name: 'noggin-section',
            component: FormlyFieldSectionWrapperComponent,
          },
        ],
        validators: [
          { name: 'phone-number', validation: PhoneValidator },
          { name: 'email', validation: Validators.email },
          { name: 'webAddress', validation: WebAddressValidator },
        ],
        validationMessages: [
          {
            name: 'phone',
            message: 'Phone numbers must start with a + followed by the country code and only numbers or spaces',
          },
          { name: 'email', message: 'Must be a valid email address' },
          { name: 'required', message: 'This field is required' },
          {
            name: 'webAddress',
            message: 'Web address must be valid and start with http:// or https://',
          },
          {
            name: 'futureDateTime',
            message: 'Invalid date, select only past date and time',
          },
          {
            name: 'pastDateTime',
            message: 'Invalid date, select only future date and time',
          },
          {
            name: 'futureDate',
            message: 'Invalid date, select only past date',
          },
          {
            name: 'pastDate',
            message: 'Invalid date, select only future date',
          },
          { name: 'max', message: maxValueError },
          { name: 'min', message: minValueError },
          { name: 'maxLength', message: maxLengthError },
          { name: 'minLength', message: minLengthError },
        ],
      },
    ]),
    { provide: MAT_DATE_LOCALE, useValue: 'en-AU' },
    ...CONTACT_SELF_SERVICE_PROVIDERS,
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: (service: ContactSelfServiceFormsService) =>
        service.getSiteKey || environment.reCaptchaSiteKey,
      deps: [ContactSelfServiceFormsService],
    },
    ReCaptchaV3Service,
    RecaptchaLoaderService,
  ],
};
